import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ServiceOverview = ({ service }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'methodology', label: 'Methodology', icon: 'Workflow' },
    { id: 'technology', label: 'Technology Stack', icon: 'Code' },
    { id: 'process', label: 'Process Timeline', icon: 'Clock' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Service Overview</h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {service.detailedDescription}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-foreground mb-3">What We Deliver</h4>
                <ul className="space-y-2">
                  {service.deliverables.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-foreground mb-3">Key Features</h4>
                <ul className="space-y-2">
                  {service.features.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Icon name="Star" size={16} className="text-warning mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
        
      case 'methodology':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Our Methodology</h3>
            <div className="space-y-4">
              {service.methodology.map((phase, index) => (
                <div key={index} className="border border-border rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground mb-2">{phase.title}</h4>
                      <p className="text-muted-foreground mb-3">{phase.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {phase.activities.map((activity, actIndex) => (
                          <span key={actIndex} className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'technology':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Technology Stack</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.technologies.map((tech, index) => (
                <div key={index} className="border border-border rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <Icon name={tech.icon} size={20} className="text-primary" />
                    <h4 className="font-semibold text-foreground">{tech.category}</h4>
                  </div>
                  <div className="space-y-2">
                    {tech.tools.map((tool, toolIndex) => (
                      <div key={toolIndex} className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{tool.name}</span>
                        <div className="flex items-center space-x-1">
                          {[...Array(5)].map((_, starIndex) => (
                            <Icon 
                              key={starIndex}
                              name="Star" 
                              size={12} 
                              className={starIndex < tool.expertise ? "text-warning fill-current" : "text-border"} 
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'process':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Process Timeline</h3>
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
              <div className="space-y-8">
                {service.timeline.map((phase, index) => (
                  <div key={index} className="relative flex items-start space-x-6">
                    <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white font-semibold text-sm relative z-10">
                      {index + 1}
                    </div>
                    <div className="flex-1 pb-8">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-foreground">{phase.phase}</h4>
                        <span className="text-sm text-primary font-medium">{phase.duration}</span>
                      </div>
                      <p className="text-muted-foreground mb-3">{phase.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {phase.milestones.map((milestone, milestoneIndex) => (
                          <span key={milestoneIndex} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                            {milestone}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="bg-white">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="p-8">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ServiceOverview;